import cn from 'clsx'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import { mediaQueryUp } from 'components/utils/breakpoint'
import config from 'constants/config'
import { useHeaderStateObserver } from 'contexts/HeaderStateContext'
import GeoContext from 'contexts/geoContext'
import React, { useContext, useRef, useState } from 'react'
import styled from 'styled-components'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import AtolProtectedBanner from '../HeaderBanners/AtolProtectedBanner'
import ContactOptionsBanner from '../HeaderBanners/ContactOptionsBanner'
import CookiesDisabledWarningBanner from '../HeaderBanners/CookiesDisabledWarningBanner'
import NotificationBanner from '../HeaderBanners/NotificationBanner'
import NavigationTertiaryHeader from './NavigationHeader/NavigationTertiaryHeader'
import SubHeaderBar from './SubHeaderBar'
import { InView } from 'react-intersection-observer'
import StickyHeaderBar from './StickyHeaderBar'
import HeaderBar from './HeaderBar'
import zIndex from 'styles/tools/z-index'
import { useLocation } from 'react-router-dom'
import PageBanner from 'components/Luxkit/Banners/PageBanner'
import TextLink from 'components/Luxkit/TextLink'

const Root = styled.header`
  position: relative;
  background: ${({ theme }) => theme.palette.neutral.eight};
  display: flex;
  flex-direction: column;
`

const AtolProtectedBannerWrapper = styled(AtolProtectedBanner)`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`
const StickyContactOptionsBanner = styled(ContactOptionsBanner)`
  position: sticky;
  top: 0;
  z-index: ${zIndex.stickyHeader + 1}; // Needed to ensure it slides over the sticky header

  ${mediaQueryUp.tablet} {
    display: none;
    position: relative;
    top: unset;
    z-index: unset;
  }
`

interface Props {
  className?: string
  notificationSlug?: string
  stickySearch?: boolean
}

function HeaderMain(props: Props) {
  const {
    className,
    notificationSlug,
    stickySearch,
  } = props

  const [scrolledPastHeader, setScrolledPastHeader] = useState<boolean>()

  const rootRef = useRef<HTMLElement>(null)
  useHeaderStateObserver('site', rootRef)
  const stickyBannerRef = useRef<HTMLDivElement>(null)
  useHeaderStateObserver('siteStickyBanner', stickyBannerRef)
  const { currentRegionCode } = useContext(GeoContext)
  const HAS_NAV_HEADER = config.businessTraveller.currentAccountMode !== 'business'
  const location = useLocation()

  return <>
    <TripGuard>
      {!location.pathname.endsWith('/ultra-lux') && config.SHOW_CONTACT_BANNER && <StickyContactOptionsBanner ref={stickyBannerRef}/>}
    </TripGuard>
    <CSSBreakpoint min="tablet">
      <NavigationTertiaryHeader />
    </CSSBreakpoint>
    <Root ref={rootRef} className={cn('PageHeader', className)}>
      <TripGuard>
        {currentRegionCode === 'GB' && <AtolProtectedBannerWrapper />}
      </TripGuard>
      {config.agentHub.isEnabled && <PageBanner
        kind="promotional-primary"
        description={
          <>Need help? Email <TextLink href="mailto:agenthubsupport@luxuryescapes.com">agenthubsupport@luxuryescapes.com</TextLink></>
        }
      />}
      {config.notification && <NotificationBanner slug={notificationSlug} />}
      <CookiesDisabledWarningBanner />
    </Root>
    { stickySearch && <StickyHeaderBar shouldAppearOnScrollUp={scrolledPastHeader} /> }
    { !stickySearch && <HeaderBar /> }
    {HAS_NAV_HEADER && <SubHeaderBar />}
    <InView onChange={(inView: boolean) => setScrolledPastHeader(!inView)} />
  </>
}

export default HeaderMain
